import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Player from '../components/player/Player'
import { bodyText, colours, playerRootSx } from '../components/player/playerStyles'
import { getEnrolment, isOk } from '../helpers/trainingApi'

const MESSAGES = {
  notFound: {
    title: 'Course not found',
    text: 'We could not find this course. It may have been withdrawn, or the link may be wrong.',
  },
  tier: {
    title: 'This course is not available',
    text: 'Online courses are not included in your organisation’s plan. Speak to your manager or training administrator.',
  },
  empty: {
    title: 'This course has no content yet',
    text: 'There is nothing to show for this course at the moment. Please try again later.',
  },
  failed: {
    title: 'We could not load this course',
    text: 'Check your connection and try again.',
  },
}

const statusOf = (res) => {
  if (isOk(res)) return res.data?.content?.modules?.some((module) => module.screens?.length) ? 'ready' : 'empty'
  if (res?.status === 404 || res?.status === 400) return 'notFound'
  if (res?.status === 403 && res.data?.errorCode === 'INSUFFICIENT_SUBSCRIPTION_TIER') return 'tier'
  return 'failed'
}

const Message = ({ status, onRetry }) => (
  <Box sx={{ ...playerRootSx, maxWidth: 640, mx: 'auto', p: { xs: 2, sm: 4 } }}>
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, borderColor: colours.hairline }} role={status === 'failed' ? 'alert' : undefined}>
      <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 1.5 }}>
        {MESSAGES[status].title}
      </Typography>
      <Typography sx={bodyText}>{MESSAGES[status].text}</Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={{ mt: 3 }}>
        {status === 'failed' && (
          <Button variant="contained" onClick={onRetry}>
            Try again
          </Button>
        )}
        <Button component={RouterLink} to="/mandatory-training" variant={status === 'failed' ? 'outlined' : 'contained'}>
          Back to my training
        </Button>
      </Stack>
    </Paper>
  </Box>
)

// The learner-facing course player: /training/course/:enrolmentId
const CoursePlayer = () => {
  const { enrolmentId } = useParams()
  const [status, setStatus] = useState('loading')
  const [data, setData] = useState(null)

  const fetchEnrolment = useCallback(async () => {
    try {
      return await getEnrolment(enrolmentId)
    } catch {
      return { status: 0 }
    }
  }, [enrolmentId])

  const load = useCallback(async () => {
    setStatus('loading')
    const res = await fetchEnrolment()
    if (isOk(res)) setData(res.data)
    setStatus(statusOf(res))
  }, [fetchEnrolment])

  // Picks up changes to the assessment and enrolment state without
  // disturbing the screen the learner is on. A failure here is ignored: the
  // next page they open asks the server again.
  const refresh = useCallback(async () => {
    const res = await fetchEnrolment()
    if (isOk(res) && res.data?.content) setData(res.data)
  }, [fetchEnrolment])

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    fetchEnrolment().then((res) => {
      if (cancelled) return
      if (isOk(res)) setData(res.data)
      setStatus(statusOf(res))
    })
    return () => {
      cancelled = true
    }
  }, [fetchEnrolment])

  if (status === 'loading') {
    return (
      <Stack role="status" alignItems="center" spacing={2} sx={{ ...playerRootSx, p: 6 }}>
        <CircularProgress aria-hidden="true" />
        <Typography sx={{ fontSize: '1.0625rem' }}>Loading your course</Typography>
      </Stack>
    )
  }
  if (status !== 'ready') return <Message status={status} onRetry={load} />

  return <Player key={enrolmentId} enrolmentId={enrolmentId} data={data} onRefresh={refresh} />
}

export default CoursePlayer
